// DIAL-UP — the plinko cabinet, skinned as a 56k modem handshake.
//
// A packet drops through eight rows of routers and lands on one of nine ports. One dial sends up
// to twenty packets, all decided by a single beacon round, so there is one wait for the batch.
//
// The modem is the hero: a beige external 56k with a real LED row — RD and SD blink while the
// round is pending, CD lights when the carrier is up. The ports are the modem's status window.
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { CHIP, dialUpLadder, dialUpWeights, revealDialUp } from 'lib/casino';
import sfx from 'lib/sfx';
import { useBet, useStakeCap } from './state';
import { FONT, GroupBox, HouseRules, Led, Outcome, Panel, PlayButton, StakeBox, T, fmtChip, parseUnits, xpButton } from './ui';

const LADDER = dialUpLadder();
const WEIGHTS = dialUpWeights();
const MAX_PACKETS = 20;
const LEDS = ['MR', 'CD', 'RD', 'SD', 'TR', 'CS', 'AA', 'HS'];

export default function DialUp({ casino }) {
  const [amount, setAmount] = useState('5');
  const [packets, setPackets] = useState(10);
  const [ports, setPorts] = useState(null);
  const bet = useBet('dialUp', casino);

  const locked = bet.phase !== 'idle' && bet.phase !== 'done';

  useEffect(() => {
    let alive = true;
    setPorts(null);
    if (!bet.result) return undefined;
    revealDialUp(bet.result.id, packets, bet.result.randomness)
      .then(p => {
        if (alive) setPorts(p);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bet.result]);

  const allIn = useMemo(() => parseUnits(amount, CHIP.decimals), [amount]);
  const stake = (allIn * BigInt(10000)) / BigInt(10500);
  const perPacket = packets > 0 ? stake / BigInt(packets) : BigInt(0);

  const hits = useMemo(() => {
    const counts = new Array(9).fill(0);
    if (ports) ports.forEach(p => (counts[p] += 1));
    return counts;
  }, [ports]);

  // Every packet of a dial lands in the same reveal, so the batch gets one clatter of chips
  // rather than twenty scheduled from a timer nobody owns.
  useEffect(() => {
    if (ports && ports.length) sfx.chipStack();
  }, [ports]);

  // The house refuses a bet whose worst case it cannot cover, and the refusal is a revert with
  // no explanation. Read the live ceiling and never let the player submit past it.
  const stakeCap = useStakeCap('dialUp', 560000, casino, amount, setAmount);
  const overCap = stakeCap !== null && stakeCap > BigInt(0) && allIn > stakeCap;
  const canPlay = casino.ready && allIn > BigInt(0) && !overCap;
  const dialing = bet.phase === 'waiting' || bet.phase === 'settling' || bet.phase === 'placing' || bet.phase === 'approving';
  const carrier = bet.phase === 'done' && ports;

  const ledOn = name => {
    if (name === 'MR' || name === 'AA') return 'on';
    if (name === 'TR' || name === 'CS') return bet.phase !== 'idle' ? 'on' : '';
    if (name === 'CD') return carrier ? 'on' : '';
    if (name === 'HS') return carrier ? 'on' : '';
    if (name === 'RD' || name === 'SD') return dialing ? 'blink' : carrier ? 'on' : '';
    return '';
  };

  return (
    <Div>
      <div className="du__modem">
        <div className="du__modem__top">
          <span className="du__brand">INTERNET 56K</span>
          <span className="du__model">V.90 FAXMODEM · EXTERNAL</span>
        </div>
        <div className="du__lcd">
          <div className="du__lcd__line">
            {bet.phase === 'idle' || bet.phase === 'done' ? 'ATDT 5551212' : 'CONNECTING…'}
          </div>
          <div className="du__lcd__status">
            {carrier
              ? `CARRIER 56000 — ${ports.length} packets delivered`
              : bet.phase === 'waiting' || bet.phase === 'settling'
              ? 'HANDSHAKE — waiting for the beacon'
              : `${packets} PACKETS READY · ${fmtChip(perPacket, 4)} ${CHIP.symbol} EACH`}
          </div>
        </div>
        <div className="du__leds">
          {LEDS.map(l => (
            <span key={l} className="du__led">
              <i className={ledOn(l)} />
              {l}
            </span>
          ))}
        </div>
      </div>

      <div className="du__ports">
        {LADDER.map((mult, i) => (
          <div key={i} className={`du__port${hits[i] ? ' hit' : ''}`}>
            <div className="du__port__mult">{(mult / 10000).toFixed(mult >= 10000 ? 1 : 2)}x</div>
            <div className="du__port__odds">{((WEIGHTS[i] / 256) * 100).toFixed(1)}%</div>
            {hits[i] ? <div className="du__port__hits">{hits[i]}</div> : null}
          </div>
        ))}
      </div>

      <Panel>
        <GroupBox legend="Packets">
          <div className="du__packets">
            <input
              id="du-packets"
              type="range"
              min={1}
              max={MAX_PACKETS}
              value={packets}
              disabled={locked}
              onChange={e => setPackets(Number(e.target.value))}
            />
            <Led text={String(packets).padStart(2, '0')} scale={0.8} />
          </div>
        </GroupBox>

        <StakeBox value={amount} onChange={setAmount} disabled={locked} />

        <PlayButton
          label="DIAL"
          onClick={() => bet.play(allIn, [packets])}
          disabled={!canPlay}
          phase={bet.phase}
          note={bet.note}
          countdown={bet.countdown}
        />

        {bet.phase === 'stuck' ? (
          <div className="du__warn">
            Your bet is placed and still open. <button onClick={bet.retrySettle}>Settle again</button>
          </div>
        ) : null}

        <Outcome result={bet.result} onAgain={bet.reset} />

        <HouseRules
          fair={false}
          practice={casino.practice}
        />
      </Panel>
    </Div>
  );
}

const Div = styled.div`
  .du__modem {
    background: linear-gradient(180deg, #ebe7d9, #d3cdb9);
    border: 2px solid #b5ae9a;
    border-radius: 6px;
    padding: 8px 10px 9px;
    box-shadow: inset 0 1px 0 #fff, 0 4px 8px rgba(0, 0, 0, 0.5);
  }
  .du__modem__top {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-bottom: 6px;
  }
  .du__brand {
    font: bold 12px ${FONT.web};
    letter-spacing: 3px;
    color: #2b2b2b;
  }
  .du__model {
    font: 8px ${FONT.web};
    letter-spacing: 1px;
    color: #6f6857;
  }
  .du__lcd {
    background: #0f2a12;
    border: 2px inset #6f6857;
    padding: 6px 10px;
    font-family: ${FONT.mono};
  }
  .du__lcd__line {
    font-size: 17px;
    color: #7cff8a;
    letter-spacing: 3px;
    text-shadow: 0 0 6px rgba(124, 255, 138, 0.7);
  }
  .du__lcd__status {
    font-size: 9px;
    color: #9fe3a6;
    margin-top: 3px;
  }
  .du__leds {
    display: flex;
    gap: 9px;
    margin-top: 8px;
    flex-wrap: wrap;
  }
  .du__led {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font: 8px ${FONT.web};
    letter-spacing: 1px;
    color: #3b3b3b;
  }
  .du__led i {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #4a1010;
    box-shadow: inset 0 0 2px #000;
  }
  .du__led i.on {
    background: #ff3b3b;
    box-shadow: 0 0 5px #ff3b3b;
  }
  .du__led i.blink {
    background: #ff3b3b;
    box-shadow: 0 0 5px #ff3b3b;
    animation: du-blink 0.25s steps(2, start) infinite;
  }
  .du__led:nth-child(odd) i.blink {
    animation-delay: 0.12s;
  }
  @keyframes du-blink {
    to {
      visibility: hidden;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .du__led i.blink {
      animation: none;
    }
  }
  .du__ports {
    display: grid;
    grid-template-columns: repeat(9, 1fr);
    gap: 3px;
    margin-top: 10px;
  }
  .du__port {
    background: #000;
    border: 1px solid #5a4200;
    box-shadow: inset 1px 1px 0 #333;
    padding: 5px 1px;
    text-align: center;
    position: relative;
  }
  .du__port.hit {
    background: ${T.gold};
    border-color: #fff3c4;
  }
  .du__port__mult {
    font: bold 10px ${FONT.mono};
    color: ${T.gold};
  }
  .du__port.hit .du__port__mult {
    color: #3b2a00;
  }
  .du__port__odds {
    font: 8px ${FONT.web};
    color: #9c8a4a;
    margin-top: 1px;
  }
  .du__port.hit .du__port__odds {
    color: #5a4200;
  }
  .du__port__hits {
    position: absolute;
    top: -7px;
    right: -3px;
    background: ${T.red};
    color: #fff;
    font: bold 9px ${FONT.web};
    padding: 0 5px;
    border-radius: 7px;
    border: 1px solid #fff;
  }
  .du__packets {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .du__packets input {
    flex: 1;
    -webkit-appearance: none;
    appearance: none;
    height: 22px;
    background: transparent;
    margin: 0;
  }
  .du__packets input::-webkit-slider-runnable-track {
    height: 4px;
    background: #fff;
    border: 1px solid #9d9da1;
    box-shadow: inset 1px 1px 0 #808080;
  }
  .du__packets input::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 11px;
    height: 21px;
    margin-top: -9px;
    background: linear-gradient(180deg, #fff, #ecebe5 86%, #d8d0c4);
    border: 1px solid #003c74;
    border-radius: 2px 2px 6px 6px;
    cursor: pointer;
  }
  .du__packets input::-moz-range-track {
    height: 4px;
    background: #fff;
    border: 1px solid #9d9da1;
  }
  .du__packets input::-moz-range-thumb {
    width: 11px;
    height: 21px;
    background: linear-gradient(180deg, #fff, #ecebe5 86%, #d8d0c4);
    border: 1px solid #003c74;
    border-radius: 2px 2px 6px 6px;
  }
  .du__warn {
    margin-top: 8px;
    font: 10px ${FONT.ui};
    color: #8a0000;
  }
  .du__warn button {
    ${xpButton};
    min-width: 0;
    min-height: 19px;
    padding: 0 8px;
    margin-left: 6px;
  }
`;
